/**
 * Risk Profile Service - API client for Investment Risk Profile operations
 * TASK-179: FinSA/MiFID II Compliant Risk Assessment
 *
 * Handles all communication with the Risk Profile backend service
 */

import axios from 'axios';
import type { CompleteRiskProfile } from '../components/risk-profile/schemas/riskProfileSchema';
import { inferEmploymentStatus } from '../constants/employment';
import { API_CONFIG } from '../config/api-config';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_CONFIG.GATEWAY_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and account ID if available
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add account ID header for RLS
  const accountId = localStorage.getItem('account_id');
  if (accountId) {
    config.headers['X-Account-ID'] = accountId;
  }

  return config;
});

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('Unauthorized access');
    }
    return Promise.reject(error);
  }
);

/**
 * Risk Profile Response Types
 */
export interface RiskProfileResponse {
  client_id: string;
  risk_profile: Partial<CompleteRiskProfile>;
  status: 'not_started' | 'draft' | 'completed';
  last_updated: string | null;
}

/** TASK-931: one entry in the append-only profile history. */
export interface RiskProfileVersion {
  version: number;
  status: 'completed' | 'cleared';
  completed_at: string | null;
  completed_by: string | null;
  signed: boolean;
}

export interface RiskProfileVersionsResponse {
  client_id: string;
  versions: RiskProfileVersion[];
  /** null when the profile has never been completed — NOT v0. */
  current: Pick<RiskProfileVersion, 'version' | 'status' | 'completed_at'> | null;
}

export interface RiskProfilePDFResponse {
  success: boolean;
  client_id: string;
  pdf_url: string;
  filename: string;
  page_count: number;
}

export interface RiskProfileExtractionResponse {
  client_id: string;
  extracted_data: Partial<CompleteRiskProfile>;
  confidence_scores: Record<string, number>;
  overall_confidence: number;
  extraction_metadata: {
    processor_used: string;
    validation_passed: boolean;
    gap_filled: boolean;
    extracted_at: string;
  };
}

/** TASK-471: strategic allocation slot (sub-asset-class granularity) */
export interface TargetAllocationSlot {
  assetClass: string;
  subAssetClass: string;
  industryGroup: string;
  region: string;
  targetWeightPct: number;
}

export interface HouseModelResponse {
  determinedProfile: string;
  driftTolerancePct: number;
  targetAllocations: TargetAllocationSlot[];
}

/**
 * Risk Profile API Operations
 */
export const riskProfileAPI = {
  /**
   * TASK-471: Default strategic SAC allocation for a determinedProfile (house model).
   * Used by the Section 7 table to seed a new client's targetAllocations.
   */
  getHouseModel: async (determinedProfile: string): Promise<HouseModelResponse> => {
    const response = await apiClient.get(`/api/risk-profile/house-model/${determinedProfile}`);
    return response.data;
  },

  /**
   * Get risk profile for a client
   * TASK-232: Automatically unwraps confidence values for form prefill
   */
  get: async (clientId: string): Promise<RiskProfileResponse> => {
    const response = await apiClient.get(`/api/risk-profile/${clientId}`);
    // TASK-602: an empty/missing body used to crash here (debug logs called
    // .slice on JSON.stringify(undefined)); return an honest empty profile.
    const data = response.data ?? { risk_profile: {} };

    // TASK-232: Unwrap confidence values from risk_profile for form prefill
    if (data.risk_profile && Object.keys(data.risk_profile).length > 0) {
      data.risk_profile = unwrapConfidenceValues(data.risk_profile);
      // Infer missing fields from available data
      data.risk_profile = inferMissingFields(data.risk_profile);
    }

    return data;
  },

  /**
   * Save risk profile data (auto-save or draft)
   */
  save: async (clientId: string, profileData: Partial<CompleteRiskProfile>): Promise<{ success: boolean; client_id: string; updated_at: string }> => {
    const response = await apiClient.post(`/api/risk-profile/${clientId}/save`, profileData);
    return response.data;
  },

  /**
   * TASK-931: the profile's version history, newest first, plus `current`.
   *
   * The profile is an append-only record — each completion writes an immutable snapshot — so
   * this is how a surface says WHICH profile it is showing. Snapshots are not included; the
   * caller asks for one by version when it wants the evidence itself.
   */
  listVersions: async (clientId: string): Promise<RiskProfileVersionsResponse> => {
    const response = await apiClient.get(`/api/risk-profile/${clientId}/versions`);
    return response.data;
  },

  /**
   * Generate PDF for risk profile questionnaire (filled with data)
   */
  generatePDF: async (clientId: string): Promise<RiskProfilePDFResponse> => {
    const response = await apiClient.post(`/api/risk-profile/${clientId}/generate-pdf`);
    return response.data;
  },

  /**
   * Generate blank PDF for risk profile questionnaire (no data)
   */
  generateBlankPDF: async (clientId: string): Promise<RiskProfilePDFResponse> => {
    const response = await apiClient.post(`/api/risk-profile/${clientId}/generate-blank-pdf`);
    return response.data;
  },

  /**
   * Extract risk profile data from signed PDF using AI
   */
  extractFromPDF: async (clientId: string, file: File): Promise<RiskProfileExtractionResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(`/api/risk-profile/${clientId}/extract`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Mark risk profile as completed
   * Called when user accepts extracted data from signed PDF or confirms manual completion
   */
  complete: async (clientId: string): Promise<{ success: boolean; client_id: string; status: string; completed_at: string }> => {
    const response = await apiClient.post(`/api/risk-profile/${clientId}/complete`);
    return response.data;
  },

  /**
   * Delete risk profile for a client
   */
  delete: async (clientId: string): Promise<{ success: boolean; client_id: string; message: string }> => {
    const response = await apiClient.delete(`/api/risk-profile/${clientId}`);
    return response.data;
  }
};

/**
 * Helper Functions
 */

// Unused helper functions removed - kept for future use if needed
// _camelToSnake and _mapFieldName were defined but never used

/**
 * Infer missing fields from available data
 * E.g., infer employmentStatus from occupation/employerName
 * TASK-258: Uses shared constants for employment keyword matching
 */
const inferMissingFields = (data: any): any => {
  if (!data) return data;

  // Section 1: Infer employmentStatus if missing
  if (data.section1) {
    const section1 = data.section1;
    if (!section1.employmentStatus) {
      const occupation = section1.occupation || '';
      const employerName = section1.employerName || '';

      // Use shared utility for employment inference
      const inferredStatus = inferEmploymentStatus(occupation, employerName);
      if (inferredStatus) {
        section1.employmentStatus = inferredStatus;
        console.log(`📋 Inferred employmentStatus: ${inferredStatus} (from occupation: ${occupation}, employer: ${employerName})`);
      }
    }

    // Ensure dependentsCount is a number
    if (section1.dependentsCount !== undefined && typeof section1.dependentsCount !== 'number') {
      section1.dependentsCount = parseInt(section1.dependentsCount, 10) || 0;
    }
  }

  // Section 2: Map educationLevel values
  if (data.section2) {
    const section2 = data.section2;
    // Map 'bachelor' to 'bachelors' if needed (schema expects 'bachelors')
    if (section2.educationLevel === 'bachelor') {
      section2.educationLevel = 'bachelors';
    }
    if (section2.educationLevel === 'master') {
      section2.educationLevel = 'masters';
    }
  }

  return data;
};

/**
 * TASK-232: Unwrap confidence values from risk profile data
 * Converts { value: X, confidence: Y, needs_review: Z } to just X
 * Keeps camelCase field names for form compatibility
 * Also extracts amount from { amount, currency } objects
 */
export const unwrapConfidenceValues = (data: any, parentKey: string = ''): any => {
  if (data === null || data === undefined) {
    return data;
  }

  // Check if this is a confidence wrapper object
  if (typeof data === 'object' && !Array.isArray(data)) {
    // Check for { value, confidence } pattern
    if ('value' in data && ('confidence' in data || 'needs_review' in data)) {
      // This is a wrapped value - extract and recursively unwrap if needed
      const unwrappedValue = unwrapConfidenceValues(data.value, parentKey);
      console.log(`  📦 Unwrapped: ${parentKey} → ${JSON.stringify(unwrappedValue).slice(0, 50)}`);
      return unwrappedValue;
    }

    // Check for { amount, currency } pattern (monetary values)
    if ('amount' in data && 'currency' in data && Object.keys(data).length === 2) {
      // Extract just the amount for form fields
      console.log(`  💰 Extracted amount: ${data.amount} ${data.currency}`);
      return data.amount;
    }

    // Regular object - recursively unwrap all properties
    const unwrapped: Record<string, any> = {};
    for (const [key, value] of Object.entries(data)) {
      // Skip metadata keys
      if (key === '_metadata') {
        continue;
      }

      // TASK-244: Keep camelCase field names (form schema expects camelCase)
      // Do NOT convert to snake_case - the form uses camelCase
      const newKey = key;

      // Determine the current section context
      const currentSection = key.startsWith('section') ? key : parentKey;

      // Recursively unwrap the value
      unwrapped[newKey] = unwrapConfidenceValues(value, currentSection);
    }
    return unwrapped;
  }

  // Array - recursively unwrap each element
  if (Array.isArray(data)) {
    return data.map(item => unwrapConfidenceValues(item, parentKey));
  }

  // Primitive value - return as-is
  return data;
};

/**
 * Format date for display (European format: DD.MM.YYYY)
 * Always uses European date format regardless of language setting
 *
 * @param date - Date object, ISO string, or null
 * @param locale - Optional locale (defaults to 'de-CH' for consistent European format)
 * @returns Formatted date string in DD.MM.YYYY format
 */
export const formatSwissDate = (date: Date | string | null, locale: string = 'de-CH'): string => {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;

  // Use Intl for proper localization while ensuring European format
  // All Swiss locales (de-CH, fr-CH, it-CH, en-GB) use DD.MM.YYYY or DD/MM/YYYY
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(d);
};

/**
 * Format currency in Swiss format (CHF 1'000.00)
 */
export const formatSwissCurrency = (amount: number): string => {
  return new Intl.NumberFormat('de-CH', {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

/**
 * TASK-658: the ONE completion formula — 10 required sections, mirroring the
 * backend's shared.risk.completion (which is the canonical server truth; the
 * API's `completion` block wins whenever present). The previous 5-section
 * any-value-counts formula was one of the audit's three contradictory numbers.
 */
export const REQUIRED_FIELDS: Record<string, string[]> = {
  section1: ['firstName', 'lastName', 'dateOfBirth', 'emailPrimary', 'phonePrimary'],
  section2: ['educationLevel', 'financeExperienceYears'],
  section3: ['portfolioAmountToManage', 'maxLossCapacity'],
  section4: ['timeHorizon', 'expectedReturn'],
  section5: ['riskAttitude', 'maxAcceptableLoss1yr'],
  section6: ['determinedProfile', 'characteristics'],
  section7: ['strategyName', 'targetAllocations'],
  section8: ['declarations'],
  section9: ['clientSignatureSigned', 'advisorSignatureSigned'],
  section10: ['reviewFrequency', 'nextReviewDate'],
};

const fieldPresent = (value: unknown): boolean => {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value as object).length > 0;
  if (typeof value === 'boolean') return value;
  return true;
};

export const calculateCompletionPercentage = (profileData: Partial<CompleteRiskProfile>): number => {
  const sectionKeys = Object.keys(REQUIRED_FIELDS);
  const complete = sectionKeys.filter((key) => {
    const section = (profileData as Record<string, Record<string, unknown> | undefined>)[key];
    if (!section) return false;
    return REQUIRED_FIELDS[key].every((field) => fieldPresent(section[field]));
  });
  return Math.round((complete.length / sectionKeys.length) * 100);
};

/**
 * Get status badge color
 */
export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'completed':
      return 'green';
    case 'draft':
      return 'yellow';
    case 'not_started':
    default:
      return 'gray';
  }
};

/**
 * Main Risk Profile Service Export
 */
// ── TASK-619: inferred answers (inferred ≠ answered until RM confirms) ──────

export interface InferredAnswerState {
  state: 'inferred' | 'confirmed';
  provenance: string;
  source_refs: string[];
  assessor?: string;
  confirmed_at?: string;
}

export interface InferredAnswersResponse {
  client_id: string;
  /** Nested partial section 2 — knowledge & experience inferred from employment/background. */
  section2: Record<string, unknown>;
  /**
   * TASK-846: partial section 3 — currently just `totalNetWorthAttested`, the net worth the
   * KYC file states. It is a figure to RECONCILE against the questionnaire's own line items,
   * never one to write into them, so it lives here and in `answer_states` and never in the
   * saved `section3` tree.
   */
  section3: Record<string, unknown>;
  answer_states: Record<string, InferredAnswerState>;
  not_derivable: Array<{ path: string; reason: string }>;
}

export async function getInferredRiskAnswers(clientId: string): Promise<InferredAnswersResponse> {
  const res = await apiClient.get<InferredAnswersResponse>(
    `/api/risk-profile/${clientId}/inferred`
  );
  return res.data;
}

export async function confirmInferredAnswers(
  clientId: string,
  confirm: Array<{ path: string; value: unknown }>,
  override: string[] = []
): Promise<{ answer_states: Record<string, InferredAnswerState> }> {
  const res = await apiClient.post(`/api/risk-profile/${clientId}/confirm-inferred`, {
    confirm,
    override,
  });
  return res.data;
}

const riskProfileService = {
  ...riskProfileAPI,

  /**
   * Auto-save with debouncing
   * Returns a debounced save function
   */
  createAutoSave: (clientId: string, delay: number = 3000) => {
    let timeoutId: ReturnType<typeof setTimeout>;

    return (profileData: Partial<CompleteRiskProfile>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        try {
          await riskProfileAPI.save(clientId, profileData);
          console.log('Risk profile auto-saved');
        } catch (error) {
          console.error('Auto-save failed:', error);
        }
      }, delay);
    };
  },

  /**
   * Validate risk profile data before submission
   */
  validate: async (profileData: Partial<CompleteRiskProfile>): Promise<{ valid: boolean; errors: string[] }> => {
    const errors: string[] = [];

    // Section 1: Personal Information (TASK-244: Updated to camelCase)
    if (!profileData.section1) {
      errors.push('Personal information is required');
    } else {
      if (!profileData.section1.firstName || !profileData.section1.lastName) errors.push('Full name is required');
      if (!profileData.section1.dateOfBirth) errors.push('Date of birth is required');
      if (!profileData.section1.emailPrimary) errors.push('Email is required');
      if (!profileData.section1.phonePrimary) errors.push('Phone is required');
    }

    // Section 2: Knowledge & Experience (TASK-244: Updated to camelCase)
    if (!profileData.section2) {
      errors.push('Investment knowledge section is required');
    } else {
      if (!profileData.section2.educationLevel) errors.push('Education level is required');
      if (!profileData.section2.financeExperienceYears) errors.push('Finance experience is required');
    }

    // Section 3: Financial Situation (TASK-244: Updated to camelCase flat structure)
    if (!profileData.section3) {
      errors.push('Financial situation is required');
    } else {
      if (!profileData.section3.portfolioAmountToManage) {
        errors.push('Portfolio amount to manage is required');
      }
      if (!profileData.section3.maxLossCapacity) {
        errors.push('Maximum loss capacity is required');
      }
    }

    // Section 4: Investment Objectives (TASK-244: Updated to camelCase)
    if (!profileData.section4) {
      errors.push('Investment objectives are required');
    } else {
      if (!profileData.section4.timeHorizon) errors.push('Time horizon is required');
      if (!profileData.section4.investmentPurposes || profileData.section4.investmentPurposes.length === 0) {
        errors.push('At least one investment purpose is required');
      }
      if (!profileData.section4.expectedReturn) errors.push('Expected return is required');
    }

    // Section 5: Risk Tolerance (TASK-244: Updated to camelCase)
    if (!profileData.section5) {
      errors.push('Risk tolerance section is required');
    } else {
      if (!profileData.section5.riskAttitude) errors.push('Risk attitude is required');
      if (!profileData.section5.portfolioDeclineReaction) {
        errors.push('Portfolio decline reaction is required');
      }
      if (!profileData.section5.maxAcceptableLoss1yr) {
        errors.push('Maximum acceptable loss is required');
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  },

  /**
   * Submit completed risk profile
   */
  submit: async (clientId: string, profileData: CompleteRiskProfile): Promise<RiskProfilePDFResponse> => {
    // Validate first
    const validation = await riskProfileService.validate(profileData);
    if (!validation.valid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    // Save with completed status
    const dataWithStatus = {
      ...profileData,
      status: 'completed',
      completion_date: new Date().toISOString()
    };

    await riskProfileAPI.save(clientId, dataWithStatus);

    // Generate PDF
    return await riskProfileAPI.generatePDF(clientId);
  },

  /**
   * Download PDF
   */
  downloadPDF: (pdfUrl: string, filename: string) => {
    // Create temporary link and trigger download
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  // Helper utilities
  formatSwissDate,
  formatSwissCurrency,
  calculateCompletionPercentage,
  getStatusColor
};

export default riskProfileService;
